import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import {
  isDrugNotFoundError,
  useDrugDetail,
} from '@/entities/drug/hooks/useDrugQueries';
import {
  displayDrugCode,
  formatFeeRate,
} from '@/entities/drug/utils/drug.utils';
import { RelatedDrugsTab } from '@/shared/components/drug-drawer/RelatedDrugsTab';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import DOMPurify from 'dompurify';
import { Fragment, useMemo } from 'react';

const VISIBLE_CLINICS = 3;

const LABEL_TABS = [
  { value: 'direction', label: '용법용량' },
  { value: 'effect', label: '효능효과' },
  { value: 'precaution', label: '주의사항' },
] as const;

/** M-12 드로어 본문 — 약 정보 · 핵심 수치 3종 · 처방 중 병의원 · 탭 4종 */
export const DrugDetailView = ({
  drugCode,
  onSelectRelated,
}: {
  drugCode: string;
  /** 동일성분 행 클릭 — 같은 드로어에서 내용 교체 (현재 약을 브레드크럼에 남긴다) */
  onSelectRelated: (
    current: { drugCode: string; drugName: string },
    nextCode: string,
  ) => void;
}) => {
  const { data, isPending, isError, error, refetch } = useDrugDetail(drugCode);

  if (isPending) return <DetailSkeleton />;
  if (isError) {
    return isDrugNotFoundError(error) ? (
      <EmptyState className="py-20" message="의약품 정보를 찾을 수 없어요." />
    ) : (
      <ErrorState className="py-20" onRetry={() => void refetch()} />
    );
  }

  const { drug } = data;
  // 100회 처방 예상액은 서버 값(제약사 기준)을 그대로 쓴다
  const expected = data.pharmaEstimatedAmount;
  const unit =
    data.unitQuantity != null && data.baseUnit
      ? `${data.unitQuantity}${data.baseUnit}`
      : null;
  const meta = [
    displayDrugCode(drug.drugCode),
    drug.pharmaCompanyName,
    drug.reimbursementType,
    data.etcOtcType,
    unit,
  ].filter((v): v is string => !!v);
  const stats = [
    { label: '약가', value: formatWon(drug.price), tone: 'text-ink' },
    {
      label: '수수료율',
      value: formatFeeRate(drug.pharmaFeeRate),
      tone: drug.pharmaFeeRate == null ? 'text-[#bbb]' : 'text-primary',
    },
    {
      label: '100회 처방 예상',
      value: formatWon(expected),
      tone: expected == null ? 'text-[#bbb]' : 'text-success',
    },
  ];
  const clinics = data.prescribingClinics;
  const restClinics = clinics.length - VISIBLE_CLINICS;

  return (
    <div className="flex flex-col px-6 py-[22px]">
      <DrugBadges
        isReferenceDrug={drug.referenceDrug}
        isBioequivalent={drug.bioequivalent}
        className="mb-2.5"
      />
      <h2 className="text-[19px] leading-7 font-bold text-ink">
        {drug.drugName}
      </h2>
      {drug.mainIngredientName && (
        <p className="mt-1.5 text-xs leading-[18px] text-ink-5">
          {drug.mainIngredientName}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-[7px] gap-y-[5px] text-[11px] leading-[17px] text-ink-5">
        {meta.map((m, i) => (
          <Fragment key={`${m}-${i}`}>
            {i > 0 && <span className="text-[#d5d5d5]">·</span>}
            <span>{m}</span>
          </Fragment>
        ))}
        {drug.minPrescriptionAmount != null && (
          <>
            <span className="text-[#d5d5d5]">·</span>
            <span className="text-warning">
              최소 처방 금액 {formatWon(drug.minPrescriptionAmount)}
            </span>
          </>
        )}
      </div>

      <div className="mt-4 flex rounded-lg bg-subtle px-[18px] py-3.5">
        {stats.map((s, i) => (
          <Fragment key={s.label}>
            {i > 0 && <span className="h-8 w-px shrink-0 bg-line-soft" />}
            <div
              className={cn('flex flex-1 flex-col gap-[5px]', i > 0 && 'pl-4')}
            >
              <span className="text-[10px] leading-[15px] text-ink-7">
                {s.label}
              </span>
              <span
                className={cn('text-[17px] leading-[26px] font-bold', s.tone)}
              >
                {s.value}
              </span>
            </div>
          </Fragment>
        ))}
      </div>

      {clinics.length > 0 && (
        <>
          <p className="mt-4 text-[11px] leading-[17px] font-medium text-ink-6">
            처방 중 병의원
          </p>
          <ul className="mt-[9px] flex flex-wrap gap-[7px]">
            {clinics.slice(0, VISIBLE_CLINICS).map((c) => (
              <li
                key={c.clinicId}
                className="rounded-[14px] border border-[#e8e8e8] bg-field px-[11px] py-1.5 text-[11px] leading-[17px] text-ink-3"
              >
                {c.clinicName}
              </li>
            ))}
            {restClinics > 0 && (
              <li
                className="rounded-[14px] border border-[#e8e8e8] bg-field px-[11px] py-1.5 text-[11px] leading-[17px] text-ink-7"
                title={clinics
                  .slice(VISIBLE_CLINICS)
                  .map((c) => c.clinicName)
                  .join(', ')}
              >
                +{restClinics}곳
              </li>
            )}
          </ul>
        </>
      )}

      <Tabs defaultValue="related" className="mt-5">
        <TabsList className="gap-0 border-b-0">
          {[{ value: 'related', label: '동일성분' }, ...LABEL_TABS].map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="mb-0 flex-1 border-b-2 border-line-soft pb-2.5 text-[13px] leading-5 text-ink-6"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="related" className="pt-[18px] outline-none">
          <RelatedDrugsTab
            drugCode={drugCode}
            onSelect={(next) =>
              onSelectRelated({ drugCode, drugName: drug.drugName }, next)
            }
          />
        </TabsContent>
        {LABEL_TABS.map((t) => (
          <TabsContent
            key={t.value}
            value={t.value}
            className="pt-5 outline-none"
          >
            <LabelHtml html={data[t.value]} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

/**
 * 허가사항 원문 렌더링에 허용하는 태그 — 나머지(스타일 · 링크 등)는 버린다
 * 이미지는 원문에 박힌 base64(data:image) 만 허용한다 — 외부 요청이 생기지 않게 (RDL-160)
 */
const LABEL_ALLOWED_TAGS = [
  'img',
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'sub',
  'sup',
  'span',
  'div',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/** 허가사항 이미지로 허용하는 src — svg 는 제외한다 */
const LABEL_IMAGE_SRC = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

/** 허가사항 전용 인스턴스 — 훅이 다른 DOMPurify 사용처에 번지지 않게 분리한다 */
const labelPurify = DOMPurify(window);
labelPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName === 'src' && !LABEL_IMAGE_SRC.test(data.attrValue)) {
    data.keepAttr = false;
  }
});

/**
 * 허가사항 원문 — 서버가 줄마다 `<p>` 로 감싸고 허용 태그만 남긴 HTML 을 내려준다 (docs/api/drugs.md)
 * 서버 정리와 별개로 프론트에서도 허용 목록으로 한 번 더 정제한 뒤 렌더링한다
 */
const LabelHtml = ({ html }: { html: string | null }) => {
  const clean = useMemo(
    () =>
      html?.trim()
        ? labelPurify
            .sanitize(html, {
              ALLOWED_TAGS: LABEL_ALLOWED_TAGS,
              ALLOWED_ATTR: ['colspan', 'rowspan', 'src', 'alt'],
            })
            .trim()
        : '',
    [html],
  );
  if (!clean)
    return <EmptyState className="py-12" message="등록된 정보가 없어요." />;
  return (
    <div
      className="rounded-lg bg-subtle px-5 py-4 text-[13px] leading-[22px] break-keep text-ink-2 [&_b]:font-bold [&_img]:h-auto [&_img]:max-w-full [&_li]:mb-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_table]:my-2 [&_table]:w-full [&_td]:border [&_td]:border-line [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-line [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_ul]:list-disc [&_ul]:pl-5"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

const DetailSkeleton = () => (
  <div className="flex flex-col gap-3 px-6 py-[22px]" aria-busy="true">
    <Skeleton className="h-6 w-28" />
    <Skeleton className="h-7 w-3/4" />
    <Skeleton className="h-4 w-1/3" />
    <Skeleton className="h-4 w-2/3" />
    <Skeleton className="mt-2 h-20 w-full rounded-lg" />
    <Skeleton className="h-8 w-1/2" />
    <Skeleton className="mt-4 h-10 w-full" />
    <Skeleton className="h-48 w-full rounded-lg" />
  </div>
);
